import React, { useEffect, useMemo, useRef, useState } from 'react';
import { PhonicsActivity } from '@/api/types';
import { useAssetCdn } from '@/context/Ui';
import { useLessonPlayer } from '@/context/LessonPlayer';
import { SymbolType, CdnObjectType } from '@/types';
import { AudioPlayer } from '@/components';
import { Character } from '@/components/lessonKit';
import { getRandomEntries, shuffleArray } from '@/utils/array';
import { CarouselSlide } from '../../Carousel';

const CLAY_FONT = 'var(--clay-font)';
const FLASHCARD_FONT = 'var(--clay-font-flashcard)';
const GRAPHEME_DISTRACTORS = 8;

type TileState = 'correct' | 'wrong' | null;

const tileBg = (state: TileState): string => {
  if (state === 'correct') {
    return 'linear-gradient(176deg,#8FD3A2 0%,#46B06A 60%,#2A8049 100%)';
  }
  if (state === 'wrong') {
    return 'linear-gradient(176deg,#F49A8B 0%,#F26A54 60%,#E5442F 100%)';
  }
  return 'linear-gradient(176deg,var(--clay-cream-hi) 0%,var(--clay-cream) 58%,#F1E0BE 100%)';
};

const tileLip = (state: TileState): string =>
  state === 'correct'
    ? 'var(--clay-green-dk)'
    : state === 'wrong'
      ? 'var(--clay-red-dk)'
      : 'var(--clay-cream-lo)';

interface LetterTileProps {
  letter: string;
  state: TileState;
  onPick: (letter: string) => void;
}

const LetterTile = ({ letter, state, onPick }: LetterTileProps) => (
  <button
    type="button"
    onClick={() => !state && onPick(letter)}
    aria-label={`Letter ${letter}`}
    className={state === 'wrong' ? 'clay-shake' : undefined}
    style={{
      position: 'relative',
      width: 118,
      height: 118,
      border: 'none',
      padding: 0,
      borderRadius: 26,
      background: tileBg(state),
      boxShadow: `0 10px 0 ${tileLip(state)}, 0 22px 28px -8px rgba(150,80,50,.24), inset 0 6px 7px rgba(255,255,255,${state ? '.4' : '.92'})`,
      cursor: state ? 'default' : 'pointer',
      transition:
        'transform 130ms var(--ease-bounce), box-shadow 160ms var(--ease), background 200ms var(--ease)',
      display: 'grid',
      placeItems: 'center',
    }}
  >
    <span
      style={{
        fontFamily: FLASHCARD_FONT,
        fontWeight: 700,
        fontSize: 74,
        lineHeight: 1,
        color: state ? '#fff' : 'var(--clay-green)',
        textShadow: state
          ? '0 3px 0 rgba(0,0,0,.18)'
          : '0 3px 0 rgba(0,0,0,.12), 0 6px 8px rgba(0,0,0,.14)',
      }}
    >
      {letter}
    </span>
    {state === 'correct' && (
      <span
        style={{
          position: 'absolute',
          top: -12,
          right: -12,
          width: 46,
          height: 46,
          borderRadius: '50%',
          background: 'linear-gradient(176deg,#F6A83B,var(--clay-orange))',
          color: '#fff',
          display: 'grid',
          placeItems: 'center',
          boxShadow:
            '0 5px 0 var(--clay-orange-dk), 0 8px 12px rgba(120,60,10,.3)',
        }}
      >
        <svg
          width="26"
          height="26"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="4.4"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M4 12.5l5 5L20 6" />
        </svg>
      </span>
    )}
  </button>
);

interface ClayMatchStepProps {
  activity: PhonicsActivity;
  sound: string;
  availableSounds: Array<string>;
  stepIdentifier: string;
  stepNumber: number;
  totalSteps: number;
}

/**
 * ClayMatchStep — the "Match the Sound to the Letter" screen from the Claude
 * Design redesign, wired into the Focus-Sounds activity. Hear the target
 * phoneme (blue clay speaker, real audio), then tap the matching grapheme in a
 * clay letter grid; correct → green tile + check badge, wrong → red + shake.
 * Registers as a normal slide so player nav / progress / metrics are untouched.
 */
export const ClayMatchStep = ({
  activity,
  sound,
  availableSounds,
  stepIdentifier,
  stepNumber,
  totalSteps,
}: ClayMatchStepProps) => {
  const { registerSlide } = useLessonPlayer();
  const playerRef = useRef<AudioPlayer>(null);

  const [playing, setPlaying] = useState(false);
  const [picked, setPicked] = useState<string | null>(null);

  const mediaUrl = useAssetCdn(sound, SymbolType.Sound, CdnObjectType.Audio);

  const options = useMemo(() => {
    const pool = Array.from(
      new Set(availableSounds.map((s) => (s === 'q' ? 'qu' : s))),
    );
    const distractors = getRandomEntries(pool, GRAPHEME_DISTRACTORS, [sound]);
    return shuffleArray([...distractors, sound]);
  }, [availableSounds, sound]);

  const solved = picked === sound;

  useEffect(() => {
    registerSlide(
      stepIdentifier,
      undefined,
      undefined,
      undefined,
      undefined,
      Math.round((stepNumber / totalSteps) * 100),
    );
  }, [registerSlide, stepIdentifier, stepNumber, totalSteps]);

  const hear = () => playerRef.current?.play();

  const pick = (letter: string) => {
    setPicked(letter);
    if (letter === sound) {
      hear();
    } else {
      setTimeout(() => setPicked((p) => (p === letter ? null : p)), 650);
    }
  };

  const stateFor = (letter: string): TileState => {
    if (letter === sound && solved) return 'correct';
    if (letter === picked && letter !== sound) return 'wrong';
    return null;
  };

  return (
    <CarouselSlide
      identifier={stepIdentifier}
      className="theme-app relative"
      classNameInner="flex h-full w-full flex-col items-center justify-center pt-8 pb-40 md:pb-44"
      ariaLabel={`${activity.type.name} — ${stepNumber} of ${totalSteps}`}
      useContainer={false}
    >
      <div
        className="absolute inset-0"
        style={{
          background:
            'radial-gradient(42% 50% at 12% 12%, rgba(255,255,255,.32), transparent 70%),' +
            'radial-gradient(44% 44% at 90% 92%, rgba(255,255,255,.20), transparent 70%),' +
            '#FEE197',
        }}
      />

      <div className="relative flex flex-col items-center gap-5 px-8">
        <div
          style={{
            fontFamily: CLAY_FONT,
            fontWeight: 800,
            fontSize: 'clamp(28px,3.2vw,50px)',
            letterSpacing: '.3px',
            color: '#173A26',
            textAlign: 'center',
          }}
        >
          Match the sound to the letter
        </div>

        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <button
            type="button"
            aria-label="Hear the sound"
            onClick={hear}
            className="clay-press clay-btn-blue"
            style={{
              width: 92,
              height: 92,
              borderRadius: '50%',
              border: 0,
              background: 'var(--clay-blue)',
              color: '#fff',
              display: 'grid',
              placeItems: 'center',
              cursor: 'pointer',
              transform: playing ? 'scale(1.06)' : 'none',
              transition: 'transform 120ms var(--ease-bounce)',
            }}
          >
            <svg width="46" height="46" viewBox="0 0 24 24" fill="currentColor">
              <path d="M4 9v6h4l5 4V5L8 9H4z" />
              <path
                d="M16 8.5a4 4 0 0 1 0 7"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.6"
                strokeLinecap="round"
              />
            </svg>
          </button>
          <span
            style={{
              fontFamily: CLAY_FONT,
              fontWeight: 700,
              fontSize: 22,
              color: 'var(--clay-blue)',
            }}
          >
            Hear the sound
          </span>
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 118px)',
            gap: 22,
          }}
        >
          {options.map((letter) => (
            <LetterTile
              key={letter}
              letter={letter}
              state={stateFor(letter)}
              onPick={pick}
            />
          ))}
        </div>
      </div>

      <div className="absolute bottom-6 left-10 flex items-end gap-4">
        <Character
          pose="point"
          width="clamp(120px,13vw,190px)"
          alt="Pip the parrot"
        />
        <div
          style={{
            position: 'relative',
            marginBottom: 40,
            maxWidth: 300,
            background: 'var(--clay-cream)',
            borderRadius: 26,
            padding: '16px 22px',
            fontFamily: CLAY_FONT,
            fontWeight: 600,
            fontSize: 'clamp(15px,1.4vw,22px)',
            lineHeight: 1.2,
            color: '#3a2a12',
            boxShadow: 'var(--clay-shadow-cream)',
          }}
        >
          {solved
            ? "Brilliant! That's the one."
            : 'Tap to hear it, then find the letter that makes the sound.'}
          <span
            style={{
              position: 'absolute',
              left: -13,
              bottom: 22,
              width: 0,
              height: 0,
              borderStyle: 'solid',
              borderWidth: '12px 18px 12px 0',
              borderColor:
                'transparent var(--clay-cream) transparent transparent',
            }}
          />
        </div>
      </div>

      <AudioPlayer
        ref={playerRef}
        src={mediaUrl || ''}
        onPlaySrc={() => setPlaying(true)}
        onEnded={() => setPlaying(false)}
      />
    </CarouselSlide>
  );
};
